import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

// Types
interface TechnicalIndicators {
    rsi_14?: number;
    sma_20?: number;
    sma_50?: number;
    volume_ratio?: number;
    variation_5d?: number;
    support?: number;
    resistance?: number;
}

interface SwingOpportunity {
    symbole: string;
    nom_societe: string;
    secteur?: string;
    cours_actuel: number;
    swing_score: number;
    signal_type: string;
    entry_price: number;
    target_price: number;
    stop_loss: number;
    position_size: number;
    position_value: number;
    risk_reward: number;
    holding_period: string;
    justification: string;
    technical: TechnicalIndicators;
}

interface SellSignal {
    symbole: string;
    nom_societe: string;
    quantity_held: number;
    avg_buy_price: number;
    current_price: number;
    unrealized_pnl: number;
    unrealized_pnl_pct: number;
    signal_strength: string;
    signal_type: string;
    recommended_action: string;
    justification: string;
    technical: TechnicalIndicators;
}

interface PortfolioSummary {
    cash_available: number;
    cash_blocked_j2: number;
    total_portfolio_value: number;
    open_swing_positions: number;
    max_position_value: number;
    risk_per_trade: number;
}

interface EligibleStock {
    symbole: string;
    nom_societe: string;
    secteur?: string;
    cours_actuel: number;
    swing_score: number;
    trend_surfer_score: number;
    liquidity_score: number;
    liquidity_grade: string;
    is_owned: boolean;
    is_swing_opportunity: boolean;
    is_trend_opportunity: boolean;
    swing_analysis: string;
    trend_analysis: string;
    technical: TechnicalIndicators & {
        sma_100?: number;
        momentum_6m_pct?: number;
        avg_turnover_20j?: number;
    };
}

interface SwingTraderData {
    generated_at: string;
    portfolio_summary: PortfolioSummary;
    opportunities: SwingOpportunity[];
    sell_signals: SellSignal[];
    eligible_universe: EligibleStock[];
    market_sentiment: string;
    disclaimer: string;
}

export const useSwingTrader = () => {
    const { token } = useAuth();
    const [data, setData] = useState<SwingTraderData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/portfolio/swing-trader`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors du chargement');
            }

            const jsonData = await response.json();
            setData(jsonData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, refetch: fetchData };
};

export type { SwingTraderData, SwingOpportunity, SellSignal, PortfolioSummary, EligibleStock };
